import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { openPdf, openHtmlDoc } from "../lib/fiscal";
import { toast } from "sonner";
import {
  FileText,
  RefreshCw,
  XCircle,
  CheckCircle2,
  AlertCircle,
  Clock,
  Search,
  Download,
  Ban,
  Loader2,
  Archive,
  Calendar,
} from "lucide-react";

const STATUS_META = {
  autorizado: {
    label: "Autorizada",
    cls: "bg-emerald-100 text-emerald-700 border-emerald-300",
    Icon: CheckCircle2,
  },
  cancelado: {
    label: "Cancelada",
    cls: "bg-slate-100 text-slate-700 border-slate-300",
    Icon: Ban,
  },
  processando_autorizacao: {
    label: "Processando",
    cls: "bg-blue-100 text-blue-700 border-blue-300",
    Icon: Clock,
  },
  erro_autorizacao: {
    label: "Rejeitada",
    cls: "bg-red-100 text-red-700 border-red-300",
    Icon: XCircle,
  },
};

function statusBadge(st) {
  const m = STATUS_META[st] || {
    label: st || "—",
    cls: "bg-slate-100 text-slate-600 border-slate-300",
    Icon: AlertCircle,
  };
  const { Icon } = m;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded-sm ${m.cls}`}
      data-testid={`fiscal-status-${st}`}
    >
      <Icon size={10} />
      {m.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Fiscal() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [cancelRow, setCancelRow] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [actingRef, setActingRef] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiscal/emissions");
      setRows(data || []);
    } catch {
      toast.error("Erro ao carregar emissões");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterType !== "all" && r.doc_type !== filterType) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !(
            (r.ref || "").toLowerCase().includes(s) ||
            (r.chave_nfe || "").toLowerCase().includes(s) ||
            (r.sale_id || "").toLowerCase().includes(s) ||
            (r.mensagem_sefaz || "").toLowerCase().includes(s)
          )
        )
          return false;
      }
      return true;
    });
  }, [rows, filterType, filterStatus, q]);

  const stats = useMemo(() => {
    const by = { total: rows.length, autorizado: 0, erro_autorizacao: 0, cancelado: 0 };
    rows.forEach((r) => {
      if (r.status in by) by[r.status]++;
    });
    return by;
  }, [rows]);

  const refresh = async (r) => {
    setActingRef(r.ref);
    try {
      await api.get(`/fiscal/status/${r.doc_type}/${r.ref}`);
      toast.success("Status atualizado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar");
    } finally {
      setActingRef(null);
    }
  };

  const downloadDanfe = (r) => {
    if (r.doc_type === "nfce") {
      return openHtmlDoc(
        `/fiscal/danfe/nfce/${r.ref}`,
        `NFCe-${r.ref}.html`,
        `Recibo NFC-e #${r.ref?.slice(-6)}`
      );
    }
    return openPdf(
      `/fiscal/danfe/${r.doc_type}/${r.ref}`,
      `DANFE-${r.ref}.pdf`
    );
  };

  const downloadXml = async (r) => {
    try {
      const resp = await api.get(`/fiscal/xml/${r.doc_type}/${r.ref}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/xml" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.ref}.xml`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao baixar XML");
    }
  };

  const downloadXmlBulk = async (params, filename) => {
    setActingRef("bulk");
    try {
      const resp = await api.get(`/fiscal/xml/bulk?${params}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/zip" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("ZIP baixado com sucesso");
    } catch (e) {
      // e.response.data pode ser um Blob de erro; extrair text
      let msg = "Erro ao baixar em lote";
      try {
        const blob = e?.response?.data;
        if (blob instanceof Blob) {
          const text = await blob.text();
          msg = JSON.parse(text)?.detail || msg;
        } else {
          msg = e?.response?.data?.detail || msg;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setActingRef(null);
    }
  };

  const confirmCancel = async () => {
    if (justificativa.trim().length < 15 || justificativa.length > 255) {
      toast.error("Justificativa deve ter entre 15 e 255 caracteres");
      return;
    }
    setActingRef(cancelRow.ref);
    try {
      await api.post(
        `/fiscal/cancel/${cancelRow.doc_type}/${cancelRow.ref}`,
        { justificativa }
      );
      toast.success("Nota cancelada");
      setCancelRow(null);
      setJustificativa("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        detail?.focus_error?.mensagem ||
        detail?.focus_error?.erros?.[0]?.mensagem ||
        (typeof detail === "string"
          ? detail
          : "Erro ao cancelar — verifique se a nota ainda está dentro do prazo.");
      toast.error(msg);
    } finally {
      setActingRef(null);
    }
  };

  const Stat = ({ label, value, cls }) => (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
        {label}
      </div>
      <div className={`font-display text-3xl font-bold ${cls}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="fiscal-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
          Emissão Fiscal
        </div>
        <h1 className="font-display text-4xl font-bold">
          Histórico NF-e / NFC-e
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Acompanhe, re-consulte status na SEFAZ, baixe DANFE/XML e cancele
          notas.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total" value={stats.total} cls="text-slate-800" />
        <Stat
          label="Autorizadas"
          value={stats.autorizado}
          cls="text-emerald-600"
        />
        <Stat
          label="Rejeitadas"
          value={stats.erro_autorizacao}
          cls="text-red-600"
        />
        <Stat
          label="Canceladas"
          value={stats.cancelado}
          cls="text-slate-500"
        />
      </div>

      {/* Backup para contabilidade */}
      <BulkXmlPanel onDownload={downloadXmlBulk} loading={actingRef === "bulk"} />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por ref, chave, sale_id ou mensagem…"
            data-testid="fiscal-search"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Tipo
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="fiscal-filter-type"
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
          >
            <option value="all">Todos</option>
            <option value="nfe">NF-e</option>
            <option value="nfce">NFC-e</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="fiscal-filter-status"
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
          >
            <option value="all">Todos</option>
            <option value="autorizado">Autorizada</option>
            <option value="processando_autorizacao">Processando</option>
            <option value="erro_autorizacao">Rejeitada</option>
            <option value="cancelado">Cancelada</option>
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="fiscal-reload"
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm text-sm flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Atualizar lista
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A1628] text-white">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Data
              </th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Tipo
              </th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Ref
              </th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Status
              </th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Mensagem SEFAZ
              </th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">
                Chave
              </th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-slate-500">
                  <Loader2 className="inline animate-spin" size={16} />{" "}
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-slate-500">
                  Nenhuma emissão encontrada.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => {
                const busy = actingRef === r.ref;
                const canDownload = r.status === "autorizado";
                const canCancel = r.status === "autorizado";
                return (
                  <tr
                    key={r.ref}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`fiscal-row-${r.ref}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                      {fmt(r.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${
                          r.doc_type === "nfe"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {r.doc_type}
                      </span>
                      {r.environment === "homologacao" && (
                        <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-600">
                          homolog.
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.ref}
                    </td>
                    <td className="px-3 py-2">{statusBadge(r.status)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[260px]">
                      <div className="line-clamp-2">
                        {r.mensagem_sefaz || "—"}
                        {r.status_sefaz ? (
                          <span className="text-slate-400 ml-1">
                            ({r.status_sefaz})
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">
                      {r.chave_nfe ? (
                        <span title={r.chave_nfe}>
                          {r.chave_nfe.slice(0, 8)}…{r.chave_nfe.slice(-6)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => refresh(r)}
                          disabled={busy}
                          title="Re-consultar status na SEFAZ"
                          data-testid={`fiscal-refresh-${r.ref}`}
                          className="p-1.5 border border-slate-300 hover:bg-slate-100 rounded-sm disabled:opacity-50"
                        >
                          {busy ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <RefreshCw size={12} />
                          )}
                        </button>
                        <button
                          onClick={() => downloadDanfe(r)}
                          disabled={!canDownload}
                          title="Abrir DANFE (PDF)"
                          data-testid={`fiscal-danfe-${r.ref}`}
                          className="p-1.5 border border-slate-300 hover:bg-slate-100 rounded-sm disabled:opacity-30"
                        >
                          <FileText size={12} />
                        </button>
                        <button
                          onClick={() => downloadXml(r)}
                          disabled={!canDownload}
                          title="Baixar XML"
                          data-testid={`fiscal-xml-${r.ref}`}
                          className="p-1.5 border border-slate-300 hover:bg-slate-100 rounded-sm disabled:opacity-30"
                        >
                          <Download size={12} />
                        </button>
                        <button
                          onClick={() => {
                            setCancelRow(r);
                            setJustificativa("");
                          }}
                          disabled={!canCancel}
                          title="Cancelar nota"
                          data-testid={`fiscal-cancel-${r.ref}`}
                          className="p-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-sm disabled:opacity-30"
                        >
                          <Ban size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Cancel modal */}
      {cancelRow && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="fiscal-cancel-modal"
        >
          <div className="bg-white rounded-sm max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-red-600 font-semibold">
                  Cancelamento
                </div>
                <h2 className="font-display text-xl font-bold">
                  Cancelar {cancelRow.doc_type.toUpperCase()}
                </h2>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                  {cancelRow.ref}
                </div>
              </div>
              <button
                onClick={() => setCancelRow(null)}
                data-testid="fiscal-cancel-modal-close"
              >
                <XCircle size={20} />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-sm mb-3">
              Cancelamento permitido pela SEFAZ em até 24h após a autorização
              (NF-e) ou conforme regra do estado (NFC-e).
            </div>

            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Justificativa (15 a 255 caracteres)
            </label>
            <textarea
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              maxLength={255}
              data-testid="fiscal-cancel-justificativa"
              placeholder="Ex: Venda cancelada a pedido do cliente por desistência do produto."
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              {justificativa.length} / 255 —{" "}
              <span
                className={
                  justificativa.length < 15 ? "text-red-500" : "text-emerald-600"
                }
              >
                {justificativa.length < 15 ? "mínimo 15" : "ok"}
              </span>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setCancelRow(null)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Voltar
              </button>
              <button
                onClick={confirmCancel}
                disabled={actingRef === cancelRow.ref}
                data-testid="fiscal-cancel-confirm"
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-1.5"
              >
                {actingRef === cancelRow.ref ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Ban size={14} />
                )}
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function BulkXmlPanel({ onDownload, loading }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [start, setStart] = useState(
    new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [end, setEnd] = useState(now.toISOString().slice(0, 10));

  const years = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear(); y++) years.push(y);

  const doMonthly = () =>
    onDownload(
      `year=${year}&month=${month}`,
      `XMLs_UPCEL_${year}-${String(month).padStart(2, "0")}.zip`
    );

  const doRange = () => {
    if (!start || !end) return;
    if (end < start) return;
    onDownload(
      `start=${start}&end=${end}`,
      `XMLs_UPCEL_${start}_a_${end}.zip`
    );
  };

  return (
    <div
      className="bg-gradient-to-br from-[#0A1628] to-[#2563EB] text-white rounded-sm p-5"
      data-testid="fiscal-bulk-panel"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center flex-shrink-0">
          <Archive size={20} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-blue-200 font-semibold">
            Backup para Contabilidade
          </div>
          <h2 className="font-display text-xl font-bold">
            Baixar XMLs em lote (ZIP)
          </h2>
          <p className="text-sm text-blue-100 mt-1">
            Gera um arquivo <code>.zip</code> com todos os XMLs autorizados no
            período (inclui <code>_manifest.csv</code> pra sua contabilidade).
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Mensal */}
        <div className="bg-white/10 rounded-sm p-4">
          <div className="text-xs uppercase tracking-wider text-blue-200 font-semibold mb-3 flex items-center gap-1">
            <Calendar size={12} /> Por mês (mais rápido)
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-blue-200 mb-1">
                Mês
              </label>
              <select
                data-testid="bulk-month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="bg-white text-slate-800 rounded-sm px-2 py-1.5 text-sm"
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-blue-200 mb-1">
                Ano
              </label>
              <select
                data-testid="bulk-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="bg-white text-slate-800 rounded-sm px-2 py-1.5 text-sm"
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              data-testid="bulk-download-month"
              onClick={doMonthly}
              disabled={loading}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center justify-center gap-1"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Baixar ZIP
            </button>
          </div>
        </div>

        {/* Range */}
        <div className="bg-white/10 rounded-sm p-4">
          <div className="text-xs uppercase tracking-wider text-blue-200 font-semibold mb-3 flex items-center gap-1">
            <Calendar size={12} /> Intervalo personalizado
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-blue-200 mb-1">
                De
              </label>
              <input
                data-testid="bulk-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="bg-white text-slate-800 rounded-sm px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-blue-200 mb-1">
                Até
              </label>
              <input
                data-testid="bulk-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="bg-white text-slate-800 rounded-sm px-2 py-1.5 text-sm"
              />
            </div>
            <button
              data-testid="bulk-download-range"
              onClick={doRange}
              disabled={loading}
              className="flex-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center justify-center gap-1 border border-white/30"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Baixar ZIP
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3 text-[11px] text-blue-100">
        💡 O ZIP inclui apenas notas <b>autorizadas</b>. Notas rejeitadas ou canceladas ficam fora.
      </div>
    </div>
  );
}
